import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import type { FinTax, FinTaxGroup } from '@/types/finance';
import { notify } from '@/core/toast/notify';

export function FinanceTaxesPage() {
  const [taxes, setTaxes] = useState<FinTax[]>([]);
  const [groups, setGroups] = useState<FinTaxGroup[]>([]);
  const [calcTaxId, setCalcTaxId] = useState<number | ''>('');
  const [calcAmount, setCalcAmount] = useState('100');
  const [calcResult, setCalcResult] = useState<number | null>(null);
  const [newTax, setNewTax] = useState({ taxCode: '', taxName: '', taxType: 'SALES' });
  const { rows: paginatedTaxes, tablePaginationProps: taxesPaginationProps } = useClientPaginatedRows(taxes);

  const load = () => {
    financeApi.listTaxes().then(setTaxes).catch((e) => showApiError(e, 'Failed to load taxes'));
    financeApi.listTaxGroups().then(setGroups).catch(() => setGroups([]));
  };

  useEffect(() => {
    load();
  }, []);

  const calculate = async () => {
    if (!calcTaxId) return;
    try {
      const amount = await financeApi.calculateTax(Number(calcTaxId), Number(calcAmount));
      setCalcResult(amount);
    } catch (e) {
      showApiError(e, 'Tax calculation failed');
    }
  };

  const createTax = async () => {
    if (!newTax.taxCode.trim() || !newTax.taxName.trim()) {
      notify.error('Tax code and name required');
      return;
    }
    try {
      await financeApi.createTax({ ...newTax, inclusive: false });
      notify.success('Tax created');
      setNewTax({ taxCode: '', taxName: '', taxType: 'SALES' });
      load();
    } catch (e) {
      showApiError(e, 'Create tax failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Taxes"
        description="Tax codes, tax groups, and live tax calculation"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Finance', to: '/finance' }, { label: 'Taxes' }]}
      />
      <div className="card p-4 mb-4 grid gap-3 md:grid-cols-4">
        <Input placeholder="Tax code" value={newTax.taxCode} onChange={(e) => setNewTax({ ...newTax, taxCode: e.target.value.toUpperCase() })} />
        <Input placeholder="Tax name" value={newTax.taxName} onChange={(e) => setNewTax({ ...newTax, taxName: e.target.value })} />
        <Select value={newTax.taxType} onChange={(v) => setNewTax({ ...newTax, taxType: v })} options={[
          { value: 'SALES', label: 'Sales' },
          { value: 'VAT', label: 'VAT' },
          { value: 'GST', label: 'GST' },
          { value: 'WITHHOLDING', label: 'Withholding' },
        ]} />
        <Button onClick={() => void createTax()}>Add tax</Button>
      </div>
      {groups.length > 0 && (
        <div className="card p-4 mb-4">
          <h3 className="font-semibold mb-2">Tax groups</h3>
          <ul className="text-sm space-y-1">
            {groups.map((g) => (
              <li key={g.id}>{g.groupCode} — {g.groupName}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="card overflow-x-auto mb-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="p-2 text-left">Code</th>
              <th className="p-2 text-left">Name</th>
              <th className="p-2 text-left">Type</th>
              <th className="p-2 text-left">Inclusive</th>
              <th className="p-2 text-left">Active</th>
            </tr>
          </thead>
          <tbody>
            {paginatedTaxes.map((t) => (
              <tr key={t.id} className="border-b">
                <td className="p-2 font-mono">{t.taxCode}</td>
                <td className="p-2">{t.taxName}</td>
                <td className="p-2">{t.taxType}</td>
                <td className="p-2">{t.inclusive ? 'Yes' : 'No'}</td>
                <td className="p-2">{t.active ? 'Yes' : 'No'}</td>
              </tr>
            ))}
            {taxes.length === 0 && (
              <tr>
                <td colSpan={5} className="p-4 text-slate-500">
                  No taxes configured — seed via Finance setup.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <TablePagination {...taxesPaginationProps} hasRows={taxes.length > 0} />
      </div>
      <div className="card p-4 max-w-md">
        <h3 className="font-semibold mb-3">Tax calculator</h3>
        <div className="space-y-2 text-sm">
          <Select
            placeholder="Select tax"
            value={calcTaxId !== '' ? String(calcTaxId) : ''}
            onChange={(v) => setCalcTaxId(v ? Number(v) : '')}
            options={taxes.map((t) => ({
              value: String(t.id),
              label: `${t.taxCode} — ${t.taxName}`,
            }))}
          />
          <Input
            type="number"
            value={calcAmount}
            onChange={(e) => setCalcAmount(e.target.value)}
            placeholder="Amount"
          />
          <button type="button" className="btn btn-primary" onClick={() => void calculate()}>
            Calculate
          </button>
          {calcResult != null && (
            <p className="text-emerald-700 font-medium">Tax amount: {calcResult.toFixed(2)}</p>
          )}
        </div>
      </div>
    </div>
  );
}
